// ThingsToDo.jsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';

// === HERO BACKGROUND ===
import heroBg from '../assets/Japan/images (13).jpeg';

// === ACTIVITY IMAGES ===
import sakuraImg from '../assets/Japan/hanami-s1279538656.jpg';
import templesImg from '../assets/Japan/160330153344-09-japan-temples.jpg';
import fujiImg from '../assets/Japan/fuji-mountain-in-autumn-822273028-5a6a8a9c3418c600363958d3.jpg';
import onsenImg from '../assets/Japan/caption.jpg';
import cuisineImg from '../assets/Japan/images (11).jpeg';
import shinkansenImg from '../assets/Japan/jbt3.png.webp';
import matsuriImg from '../assets/Japan/content_01_5ce63a8d72.jpg';
import themeParkImg from '../assets/Japan/images (12).jpeg';
import shoppingImg from '../assets/Japan/a0000371_parts_6836bd87852f8.webp';
import seasonsImg from '../assets/Japan/ORIFUSI_1.webp';

const ThingsToDo = () => {
    const { t } = useTranslation('thingstodo');
    const [selectedActivity, setSelectedActivity] = useState(null);

    const activities = [
        {
            id: 1,
            key: 'sakuraViewing',
            image: sakuraImg,
            emoji: '🌸',
            category: 'Seasonal',
            color: '#FF6B9D'
        },
        {
            id: 2,
            key: 'ancientTemples',
            image: templesImg,
            emoji: '🏯',
            category: 'Cultural',
            color: '#9B59B6'
        },
        {
            id: 3,
            key: 'mountFuji',
            image: fujiImg,
            emoji: '🗻',
            category: 'Nature',
            color: '#2ECC71'
        },
        {
            id: 4,
            key: 'onsenRelax',
            image: onsenImg,
            emoji: '♨️',
            category: 'Wellness',
            color: '#00D4FF'
        },
        {
            id: 5,
            key: 'japaneseCuisine',
            image: cuisineImg,
            emoji: '🍣',
            category: 'Food',
            color: '#FF9F1C'
        },
        {
            id: 6,
            key: 'shinkansen',
            image: shinkansenImg,
            emoji: '🚄',
            category: 'Adventure',
            color: '#E74C3C'
        },
        {
            id: 7,
            key: 'matsuriFestivals',
            image: matsuriImg,
            emoji: '🎎',
            category: 'Cultural',
            color: '#F1C40F'
        },
        {
            id: 8,
            key: 'themeParks',
            image: themeParkImg,
            emoji: '🎢',
            category: 'Entertainment',
            color: '#8E44AD'
        },
        {
            id: 9,
            key: 'shoppingJapan',
            image: shoppingImg,
            emoji: '🛍️',
            category: 'Shopping',
            color: '#27AE60'
        },
        {
            id: 10,
            key: 'fourSeasons',
            image: seasonsImg,
            emoji: '🍁',
            category: 'Nature',
            color: '#3498DB'
        }
    ];

    return (
        <div className="min-h-screen overflow-hidden bg-[#07111f] text-white">
            {/* HERO SECTION */}
            <div className="relative flex h-[70vh] w-full items-center justify-center overflow-hidden">
                <img
                    src={heroBg}
                    alt="Things to Do in Japan"
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#07111f]/90 via-[#07111f]/75 to-[#07111f]/40" />

                <div className="container relative z-10 px-4 text-center">

                    <h1 className="font-serif text-[35px] font-semibold leading-tight text-white md:text-[65px] lg:text-[85px]">
                        {t('heroTitle')}
                    </h1>
                    <p className="mx-auto mt-6 max-w-3xl text-lg text-white/90 md:text-2xl">
                        {t('heroSubtitle')}
                    </p>
                </div>
            </div>

            {/* ACTIVITIES GRID */}
            <section className="py-24 lg:py-32">
                <div className="container mx-auto px-4">
                    <div className="mb-16 max-w-3xl">

                        <h2 className="mt-4 font-serif text-4xl font-semibold text-white md:text-5xl lg:text-6xl">
                            {t('sectionTitle')}
                        </h2>
                        <p className="mt-5 text-lg leading-8 text-white/80">
                            {t('sectionDescription') || 'A curated collection of unforgettable experiences, from seasonal blossoms to high-speed rail adventures.'}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                        {activities.map((item, idx) => {
                            const title = t(`${item.key}.title`) || item.key;
                            const description = t(`${item.key}.description`) || '';
                            return (
                                <motion.div
                                    key={item.id}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ delay: idx * 0.08 }}
                                    whileHover={{ y: -12, scale: 1.02 }}
                                    className="group relative h-[480px] cursor-pointer overflow-hidden rounded-[1.5rem] border border-white/10 bg-[#0f1d33] shadow-[0_20px_50px_rgba(0,0,0,0.25)]"
                                    onClick={() => setSelectedActivity(item)}
                                >
                                    <img
                                        src={item.image}
                                        alt={title}
                                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-[#07111f]/95 via-[#07111f]/35 to-transparent" />

                                    <div className="absolute bottom-0 left-0 right-0 p-8">
                                        <span className="mb-3 block text-5xl">{item.emoji}</span>
                                        <h3 className="mb-3 text-2xl font-semibold text-[#7fd3ff]">{title}</h3>
                                        <p className="mb-6 line-clamp-3 text-white/80">{description}</p>

                                        <button className="rounded-full bg-gradient-to-r from-[#7fd3ff] to-[#f7d774] px-8 py-3 font-semibold text-white transition-all hover:opacity-90">
                                            {t('viewMore')} →
                                        </button>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* DETAIL MODAL */}
            <AnimatePresence>
                {selectedActivity && (
                    <div
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
                        onClick={() => setSelectedActivity(null)}
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            className="relative max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-[1.5rem] border border-white/10 bg-[#0f1d33]"
                            onClick={e => e.stopPropagation()}
                        >
                            <button
                                onClick={() => setSelectedActivity(null)}
                                aria-label="Close"
                                className="absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-[#07111f]/80 text-2xl text-white backdrop-blur transition hover:bg-[#7fd3ff] hover:text-[#07111f]"
                            >
                                ×
                            </button>

                            <img
                                src={selectedActivity.image}
                                alt={t(selectedActivity.key).title}
                                className="w-full max-h-[65vh] object-contain bg-[#07111f]"
                            />

                            <div className="p-10">
                                <h2 className="mb-6 text-4xl font-semibold text-white">
                                    {t(`${selectedActivity.key}.title`)}
                                </h2>

                                <div className="space-y-6 text-lg leading-relaxed text-white/80">
                                    <p>{t(`${selectedActivity.key}.para1`)}</p>
                                    <p>{t(`${selectedActivity.key}.para2`)}</p>
                                    <p>{t(`${selectedActivity.key}.para3`)}</p>
                                </div>


                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ThingsToDo;